import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  FormGroup,
  Badge,
  InputGroup,
  InputGroupText
} from 'reactstrap';
import { 
  FaImages, 
  FaSearch, 
  FaEye, 
  FaSearchPlus, 
  FaSearchMinus, 
  FaTimes,
  FaDownload,
  FaExpand,
  FaFilter,
  FaSort
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import './GaleriaImagenes.css';

// Importar la función del backend
import { getImagenesCuentaContrato } from '../../helpers/backend';

const GaleriaImagenes = () => {
  const [imagenes, setImagenes] = useState([]);
  const [imagenesFiltradas, setImagenesFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cuentaContrato, setCuentaContrato] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('fecha');
  const [filterType, setFilterType] = useState('todos');

  // Función para obtener la URL base del servidor
  const getBaseUrl = () => {
    const URL = process.env.REACT_APP_API_URL;
    const url = URL.split("/api");
    return url[0];
  };

  const buscarImagenes = async () => {
    if (!cuentaContrato.trim()) {
      setError('Por favor ingrese una cuenta contrato válida');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await getImagenesCuentaContrato(cuentaContrato);
      console.log('Response completo:', response);

      if (response && response.data && response.data.imagenes) {
        const imagenesArray = Array.isArray(response.data.imagenes) ? response.data.imagenes : [];

        // Procesar las imágenes para construir URLs completas y formatear fechas
        const imagenesConUrlCompleta = imagenesArray.map(imagen => ({
          ...imagen,
          image_url_completa: `${getBaseUrl()}${imagen.image_url}`,
          fecha_formateada: imagen.fecha ? new Date(imagen.fecha).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Fecha no disponible',
          created_date_formateada: imagen.created_date ? new Date(imagen.created_date).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) : 'Fecha no disponible'
        }));

        console.log('Imágenes procesadas:', imagenesConUrlCompleta);
        setImagenes(imagenesConUrlCompleta);
        setImagenesFiltradas(imagenesConUrlCompleta);

        if (imagenesConUrlCompleta.length === 0) {
          setError('No se encontraron imágenes para esta cuenta contrato');
        }
      } else {
        setImagenes([]);
        setImagenesFiltradas([]);
        setError('No se encontraron imágenes para esta cuenta contrato');
      }
    } catch (error) {
      console.error('Error al buscar imágenes:', error);
      setError('Error al cargar las imágenes. Por favor intente nuevamente.');
      setImagenes([]);
      setImagenesFiltradas([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar y ordenar imágenes
  useEffect(() => {
    let filtered = [...imagenes];

    // Filtrar por término de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(imagen =>
        imagen.caso_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        imagen.tipo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        imagen.id?.toString().includes(searchTerm)
      );
    }

    // Filtrar por tipo
    if (filterType !== 'todos') {
      filtered = filtered.filter(imagen => imagen.tipo === filterType);
    }

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'fecha':
          return new Date(b.fecha || b.created_date) - new Date(a.fecha || a.created_date);
        case 'nombre':
          return (a.caso_name || '').localeCompare(b.caso_name || '');
        case 'tipo':
          return (a.tipo || '').localeCompare(b.tipo || '');
        default:
          return 0;
      }
    });

    setImagenesFiltradas(filtered);
  }, [imagenes, searchTerm, sortBy, filterType]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      buscarImagenes();
    }
  };

  const openImageModal = (imagen) => {
    setSelectedImage(imagen);
    setZoomLevel(1);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1);
  };

  const downloadImage = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'imagen.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar imagen:', error);
    }
  };

  // Obtener tipos únicos para el filtro
  const tiposUnicos = [...new Set(imagenes.map(img => img.tipo).filter(Boolean))];

  return (
    <div className="page-content galeria-imagenes">
      <Container fluid>
        {/* Header Mejorado */}
        <motion.div 
          className="gallery-header mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <div className="gallery-icon-wrapper me-3">
                  <FaImages size={28} className="text-white" />
                </div>
                <div>
                  <h1 className="gallery-title mb-1">Galería de Imágenes</h1>
                  <p className="gallery-subtitle mb-0">
                    Consulta y gestiona imágenes por cuenta contrato
                  </p>
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <Badge color="primary" className="fs-6">
                {imagenes.length} imágenes en sistema
              </Badge>
            </Col>
          </Row>
        </motion.div>

        {/* Search Section Mejorada */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="gallery-search-card mb-4 border-0 shadow-sm">
            <CardBody>
              <Row className="align-items-end">
                <Col md={6} lg={4}>
                  <FormGroup>
                    <Label for="cuentaContrato" className="form-label fw-semibold">
                      <FaSearch className="me-2" />
                      Número de Cuenta Contrato
                    </Label>
                    <InputGroup>
                      <Input
                        type="text"
                        id="cuentaContrato"
                        placeholder="Ej: CC123456789"
                        value={cuentaContrato}
                        onChange={(e) => setCuentaContrato(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="gallery-input"
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md={3} lg={2}>
                  <Button
                    color="primary"
                    onClick={buscarImagenes}
                    disabled={loading || !cuentaContrato.trim()}
                    className="gallery-search-btn w-100"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Buscando...
                      </>
                    ) : (
                      <>
                        <FaSearch className="me-2" />
                        Buscar Imágenes
                      </>
                    )}
                  </Button>
                </Col>
                <Col md={3} lg={2}>
                  <Button
                    color="outline-secondary"
                    onClick={() => {
                      setCuentaContrato('');
                      setImagenes([]);
                      setImagenesFiltradas([]);
                      setError('');
                    }}
                    className="w-100"
                  >
                    <FaTimes className="me-2" />
                    Limpiar
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </motion.div>

        {/* Error Alert Mejorado */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Alert color="danger" className="gallery-alert border-0 mb-4">
                <div className="d-flex align-items-center">
                  <FaTimes className="me-2 fs-5" />
                  <div>
                    <strong>Error:</strong> {error}
                  </div>
                </div>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filtros y Ordenamiento */}
        {imagenes.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="gallery-filters-card mb-4 border-0 shadow-sm">
              <CardBody className="py-3">
                <Row className="align-items-center g-3">
                  <Col md={4}>
                    <FormGroup className="mb-0">
                      <InputGroup size="sm">
                        <InputGroupText>
                          <FaSearch />
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Buscar en imágenes..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Input
                        type="select"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        size="sm"
                      >
                        <option value="todos">Todos los tipos</option>
                        {tiposUnicos.map(tipo => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Input
                        type="select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        size="sm"
                      >
                        <option value="fecha">Ordenar por fecha</option>
                        <option value="nombre">Ordenar por nombre</option>
                        <option value="tipo">Ordenar por tipo</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <div className="text-muted small">
                      {imagenesFiltradas.length} de {imagenes.length} imágenes
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Results Section Mejorada */}
        {imagenes.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="gallery-results-card border-0 shadow-sm">
              <CardBody>
                <div className="gallery-results-header mb-4">
                  <Row className="align-items-center">
                    <Col>
                      <h4 className="mb-0 text-dark">
                        <FaImages className="me-2 text-primary" />
                        {imagenesFiltradas.length} imagen{imagenesFiltradas.length !== 1 ? 'es' : ''} encontrada{imagenesFiltradas.length !== 1 ? 's' : ''}
                      </h4>
                    </Col>
                    <Col xs="auto">
                      <Badge color="success" className="fs-6">
                        Cuenta: {cuentaContrato}
                      </Badge>
                    </Col>
                  </Row>
                </div>

                <AnimatePresence>
                  {imagenesFiltradas.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="no-results text-center py-5"
                    >
                      <FaFilter size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">No hay imágenes que coincidan con los filtros</h5>
                      <p className="text-muted">Intenta ajustar los criterios de búsqueda</p>
                    </motion.div>
                  ) : (
                    <Row>
                      {imagenesFiltradas.map((imagen, index) => (
                        <Col lg={4} md={6} key={imagen.id || index} className="mb-4">
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            whileHover={{ y: -5 }}
                          >
                            <Card className="gallery-image-card h-100 border-0 shadow-sm">
                              <div className="gallery-image-wrapper position-relative">
                                <img
                                  src={imagen.image_url_completa}
                                  alt={`Imagen ${imagen.id} - ${imagen.caso_name}`}
                                  className="gallery-image"
                                  loading="lazy"
                                  onError={(e) => {
                                    console.error('Error al cargar imagen:', imagen.image_url_completa);
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjh mOWZhIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTNmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPs6XIMOxIEdlbmVyYWRhPC90ZXh0Pjwvc3ZnPg==';
                                  }}
                                />
                                <div className="gallery-image-overlay">
                                  <div className="gallery-image-actions">
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="gallery-action-btn me-2"
                                      onClick={() => openImageModal(imagen)}
                                      title="Ver imagen en detalle"
                                    >
                                      <FaEye />
                                    </Button>
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="gallery-action-btn"
                                      onClick={() => downloadImage(imagen.image_url_completa, `imagen-${imagen.id}.jpg`)}
                                      title="Descargar imagen"
                                    >
                                      <FaDownload />
                                    </Button>
                                  </div>
                                </div>
                                <div className="gallery-image-badges">
                                  <Badge color="primary" className="me-1">
                                    ID: {imagen.id}
                                  </Badge>
                                  {imagen.tipo && (
                                    <Badge color="info">
                                      {imagen.tipo}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <CardBody className="gallery-image-info">
                                <h6 className="gallery-image-title mb-2">
                                  {imagen.caso_name || 'Sin nombre'}
                                </h6>
                                <div className="gallery-image-meta">
                                  <small className="text-muted d-block">
                                    <strong>Subido:</strong> {imagen.created_date_formateada}
                                  </small>
                                  {imagen.fecha && (
                                    <small className="text-muted d-block">
                                      <strong>Fecha imagen:</strong> {imagen.fecha_formateada}
                                    </small>
                                  )}
                                </div>
                              </CardBody>
                            </Card>
                          </motion.div>
                        </Col>
                      ))}
                    </Row>
                  )}
                </AnimatePresence>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Image Modal Mejorado */}
        <Modal 
          isOpen={modalOpen} 
          toggle={closeModal} 
          size="xl" 
          className="gallery-modal"
          backdrop="static"
        >
          <ModalHeader toggle={closeModal} className="gallery-modal-header bg-dark text-white border-0">
            <div className="d-flex align-items-center">
              <FaImages className="me-2" />
              <div>
                <div className="fw-bold">{selectedImage?.caso_name || 'Imagen'}</div>
                <small className="text-white-50">
                  ID: {selectedImage?.id} | Tipo: {selectedImage?.tipo}
                </small>
              </div>
            </div>
            <div className="gallery-modal-controls">
              <Button
                color="outline-light"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.5}
                className="me-1"
              >
                <FaSearchMinus />
              </Button>
              <Button
                color="outline-light"
                size="sm"
                onClick={resetZoom}
                className="me-1"
              >
                {Math.round(zoomLevel * 100)}%
              </Button>
              <Button
                color="outline-light"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 3}
                className="me-2"
              >
                <FaSearchPlus />
              </Button>
              {selectedImage && (
                <Button
                  color="outline-light"
                  size="sm"
                  onClick={() => downloadImage(selectedImage.image_url_completa, `imagen-${selectedImage.id}.jpg`)}
                >
                  <FaDownload />
                </Button>
              )}
            </div>
          </ModalHeader>
          <ModalBody className="gallery-modal-body p-0 bg-dark">
            {selectedImage && (
              <div className="gallery-modal-image-wrapper">
                <motion.img
                  src={selectedImage.image_url_completa}
                  alt={selectedImage.caso_name}
                  className="gallery-modal-image"
                  style={{ transform: `scale(${zoomLevel})` }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  drag
                  dragConstraints={{
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                  }}
                />
              </div>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default GaleriaImagenes;